import { useState } from 'react';
import {
  GripVertical,
  User,
  Users,
  Plus,
  X,
  ChevronDown,
  ChevronUp,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { ApprovalLevel, ApprovalLevelLogic, ApproverCandidate, ApprovalRule } from '@/api/services';
import { ApprovalRuleBuilder } from './ApprovalRuleBuilder';
import { cn } from '@/lib/utils';

interface ApprovalLevelCardProps {
  level: ApprovalLevel;
  index: number;
  approverCandidates: ApproverCandidate[];
  onUpdate: (level: ApprovalLevel) => void;
  onDelete: () => void;
  isDragging?: boolean;
}

export function ApprovalLevelCard({
  level,
  index,
  approverCandidates,
  onUpdate,
  onDelete,
  isDragging,
}: ApprovalLevelCardProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const [showApproverSelect, setShowApproverSelect] = useState(false);

  const handleNameChange = (name: string) => {
    onUpdate({ ...level, name });
  };

  const handleLogicChange = (logic: ApprovalLevelLogic) => {
    onUpdate({ ...level, logic });
  };

  const handleAddApprover = (userId: string) => {
    const candidate = approverCandidates.find((c) => c.id === userId);
    if (!candidate) return;

    // Check if already added
    if (level.approvers.some((a) => a.userId === userId)) return;

    const newApprover = {
      id: `temp-${Date.now()}`,
      userId,
      user: {
        id: candidate.id,
        fullName: candidate.fullName,
        email: candidate.email,
      },
    };

    onUpdate({
      ...level,
      approvers: [...level.approvers, newApprover],
      approverIds: [...(level.approverIds || []), userId],
    });
    setShowApproverSelect(false);
  };

  const handleRemoveApprover = (userId: string) => {
    onUpdate({
      ...level,
      approvers: level.approvers.filter((a) => a.userId !== userId),
      approverIds: (level.approverIds || []).filter((id) => id !== userId),
    });
  };

  const handleRulesChange = (rules: ApprovalRule[]) => {
    onUpdate({ ...level, rules });
  };

  const availableApprovers = approverCandidates.filter(
    (c) => !level.approvers.some((a) => a.userId === c.id)
  );

  return (
    <Card
      className={cn(
        'transition-all',
        isDragging && 'opacity-50 ring-2 ring-primary'
      )}
    >
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <CardHeader className="p-4">
          <div className="flex items-center gap-3">
            <div className="cursor-grab text-muted-foreground hover:text-foreground">
              <GripVertical className="w-5 h-5" />
            </div>

            <Badge variant="outline" className="font-mono">
              Level {index + 1}
            </Badge>

            <Input
              value={level.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Level name"
              className="flex-1 max-w-xs"
            />

            <div className="flex items-center gap-2 ml-auto">
              <Badge variant="secondary">
                <Users className="w-3 h-3 mr-1" />
                {level.approvers.length} approver{level.approvers.length !== 1 ? 's' : ''}
              </Badge>

              <Button
                variant="ghost"
                size="icon"
                onClick={onDelete}
                className="text-muted-foreground hover:text-destructive"
              >
                <Trash2 className="w-4 h-4" />
              </Button>

              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="icon">
                  {isExpanded ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </Button>
              </CollapsibleTrigger>
            </div>
          </div>
        </CardHeader>

        <CollapsibleContent>
          <CardContent className="p-4 pt-0 space-y-6">
            {/* Approval Logic */}
            <div className="space-y-2">
              <Label>Approval Logic</Label>
              <Select value={level.logic} onValueChange={handleLogicChange}>
                <SelectTrigger className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Anyone">
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      <span>Anyone can approve</span>
                    </div>
                  </SelectItem>
                  <SelectItem value="Everyone">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      <span>Everyone must approve</span>
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {level.logic === 'Anyone'
                  ? 'Only one approver needs to approve for this level to pass'
                  : 'All approvers must approve for this level to pass'}
              </p>
            </div>

            {/* Approvers */}
            <div className="space-y-3">
              <Label>Approvers</Label>
              <div className="flex flex-wrap gap-2">
                {level.approvers.map((approver) => (
                  <Badge
                    key={approver.userId}
                    variant="secondary"
                    className="pl-2 pr-1 py-1 flex items-center gap-2"
                  >
                    <User className="w-3 h-3" />
                    <span>{approver.user?.fullName || 'Unknown'}</span>
                    <button
                      onClick={() => handleRemoveApprover(approver.userId)}
                      className="ml-1 hover:bg-muted rounded-full p-0.5"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}

                {showApproverSelect ? (
                  <div className="flex items-center gap-2">
                    <Select onValueChange={handleAddApprover}>
                      <SelectTrigger className="w-64">
                        <SelectValue placeholder="Select approver" />
                      </SelectTrigger>
                      <SelectContent>
                        {availableApprovers.length > 0 ? (
                          availableApprovers.map((candidate) => (
                            <SelectItem key={candidate.id} value={candidate.id}>
                              <div className="flex flex-col">
                                <span>{candidate.fullName}</span>
                                <span className="text-xs text-muted-foreground">
                                  {candidate.email}
                                </span>
                              </div>
                            </SelectItem>
                          ))
                        ) : (
                          <SelectItem value="none" disabled>
                            No more approvers available
                          </SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setShowApproverSelect(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowApproverSelect(true)}
                    disabled={availableApprovers.length === 0}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Add Approver
                  </Button>
                )}
              </div>
              {level.approvers.length === 0 && (
                <p className="text-sm text-amber-600">
                  At least one approver is required
                </p>
              )}
            </div>

            {/* Rules */}
            <div className="space-y-3">
              <Label>Conditional Rules (Optional)</Label>
              <p className="text-xs text-muted-foreground">
                If rules are defined, this level only applies when at least one
                rule matches
              </p>
              <ApprovalRuleBuilder
                rules={level.rules}
                onChange={handleRulesChange}
              />
            </div>
          </CardContent>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
}
